import React, { useState, useEffect } from "react";
import { EditButton, ShowButton, DeleteButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Table, Input, Space, Tag, Card } from "antd";
import { SearchOutlined, PlusOutlined, ShopOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";

interface Vendor {
    id: number;
    name: string;
    vatNumber?: string;
    tpin?: string;
    description?: string;
    isActive: boolean;
    createdAt: string;
}

export const VendorList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<Vendor[]>([]);
    const navigate = useNavigate();

    const { data, isLoading } = useList({
        resource: "vendors",
    });

    useEffect(() => {
        if (!data?.data) return;

        let vendors = data.data as Vendor[];
        
        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            vendors = vendors.filter((vendor: Vendor) =>
                vendor.name?.toLowerCase().includes(searchLower) ||
                vendor.description?.toLowerCase().includes(searchLower) ||
                vendor.tpin?.toLowerCase().includes(searchLower) ||
                vendor.vatNumber?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...vendors].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value}</span>
            ),
            sorter: (a: Vendor, b: Vendor) => a.name.localeCompare(b.name),
        },
        {
            title: "VAT Number",
            dataIndex: "vatNumber",
            key: "vatNumber",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
            responsive: ['lg'] as any,
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "status",
            render: (value: boolean) => (
                <Tag color={value ? "success" : "default"} className="status-tag">
                    {value ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            sorter: (a: Vendor, b: Vendor) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            responsive: ['md'] as any,
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_: any, record: Vendor) => (
                <Space size="small">
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    <EditButton hideText size="small" recordItemId={record.id} />
                    <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Vendors"
                icon={<ShopOutlined />}
                breadcrumbs={[{ title: "Vendors" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/vendors/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by name, VAT number, or description"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 800 }}
                    />
                </Card>
            </div>
        </div>
    );
};
